import { careManifest } from "@shared/schemes/manifests";
import type { PremiseConfig, SchemeTokenOverrides } from "@shared/types/foyer";

/**
 * The colours + fonts a door sign renders with. Derived from the care template
 * ("Hemvist") so a corridor sign reads as part of the same family as the in-room
 * TVs of the same premise - warm paper, deep green, Figtree.
 */
export interface DoorSignPalette {
  paper: string;
  paper2: string;
  surface: string;
  hair: string;
  ink: string;
  inkSoft: string;
  inkMuted: string;
  gDeep: string;
  gCore: string;
  gSoft: string;
  gTint: string;
  gGlow: string;
  sans: string;
  mono: string;
}

/**
 * Build the door-sign palette from the care manifest defaults, overlaid with the
 * premise's care-scheme token overrides when it has one bound (so an operator who
 * recoloured their care TVs gets matching signs). Self-contained - no scheme
 * context required, since a door sign renders outside the SchemeRouter.
 */
export function doorSignPalette(
  config?: Pick<PremiseConfig, "scheme">
): DoorSignPalette {
  const d = careManifest.defaultTokens;
  const o: SchemeTokenOverrides | undefined =
    config?.scheme?.key === "care" ? config.scheme.tokens : undefined;
  const pal = { ...d.palette, ...(o?.palette ?? {}) };
  const ex = { ...d.extras, ...(o?.extras ?? {}) } as Record<string, string>;
  const fonts = { ...d.fonts, ...(o?.fonts ?? {}) };
  return {
    paper: pal.bg,
    paper2: ex.paper2,
    surface: ex.surface,
    hair: pal.rule ?? ex.hair,
    ink: pal.ink,
    inkSoft: pal.inkDim ?? "#5F665C",
    inkMuted: ex.inkMuted,
    gDeep: pal.accent2 ?? "#1F3D2A",
    gCore: pal.accent,
    gSoft: ex.gSoft,
    gTint: ex.gTint,
    gGlow: ex.gGlow,
    sans: fonts.sans ?? '"Figtree", system-ui, sans-serif',
    mono: fonts.mono ?? '"JetBrains Mono", ui-monospace, monospace',
  };
}
